"use client";

import React, { useState, useEffect } from "react";
import { User, Mail, Globe, Clock, Save, X, Shield, Palette, Check, Languages } from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { useTheme } from "@/lib/theme/context";
import { useLanguage } from "@/lib/i18n/context";
import { SUPPORTED_LANGUAGES, Language } from "@/lib/i18n/types";
import { ThemeId } from "@/lib/theme/themes";
import { useToast } from "@/components/ui/Toast";
import { COMMON_TIMEZONES, formatDateTime } from "@/lib/datetime";

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserProfileModal({ isOpen, onClose }: UserProfileModalProps) {
  const { user, updateProfile } = useAuth();
  const { themeId, themes, setTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const toast = useToast();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const [selectedTheme, setSelectedTheme] = useState<ThemeId>(themeId);
  const [saving, setSaving] = useState(false);
  const [currentTimePreview, setCurrentTimePreview] = useState("");

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || "");
      setEmail(user.email || "");
      setTimezone(user.timezone || "UTC");
      setSelectedTheme(themeId);
      setSelectedLanguage(language);
    }
  }, [user, isOpen, themeId, language]);

  useEffect(() => {
    const updatePreview = () => {
      setCurrentTimePreview(formatDateTime(new Date(), timezone));
    };
    updatePreview();
    const timer = setInterval(updatePreview, 1000);
    return () => clearInterval(timer);
  }, [timezone]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      setTheme(selectedTheme);
      setLanguage(selectedLanguage);
      const res = await updateProfile({
        displayName: displayName.trim() || undefined,
        email: email.trim() || undefined,
        timezone,
      });

      if (res.success) {
        toast.success(t("auth.profile.title"), t("auth.profile.success"));
        onClose();
      } else {
        toast.error(t("common.status.error"), res.error || t("common.notifications.actionFailed"));
      }
    } catch {
      toast.error(t("common.status.error"), t("common.notifications.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-surface-overlay backdrop-blur-sm animate-in fade-in-50 zoom-in-95 duration-150">
      <div className="bg-surface-card border border-themed-border rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-themed-borderSubtle bg-surface-muted/30">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 text-[var(--accent-primary)]">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-themed-textPrimary">{t("auth.profile.title")}</h2>
              <p className="text-xs text-themed-textSecondary">{t("auth.profile.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-themed-textMuted hover:text-themed-textPrimary rounded-lg hover:bg-surface-cardHover transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Read-Only Account Info Banner */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-muted border border-themed-border text-xs">
            <div>
              <span className="text-themed-textMuted">{t("auth.profile.username")}: </span>
              <span className="font-mono text-themed-textPrimary font-medium">{user.username}</span>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 text-[var(--accent-primary)] text-[11px] font-semibold">
              <Shield className="w-3 h-3" /> {user.role}
            </span>
          </div>

          {/* Language Selection */}
          <div>
            <label className="block text-xs font-semibold text-themed-textPrimary mb-1.5 flex items-center gap-1.5">
              <Languages className="w-4 h-4 text-[var(--accent-primary)]" />
              <span>{t("auth.profile.language")}</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = lang.code === selectedLanguage;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setSelectedLanguage(lang.code)}
                    className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all ${
                      isSelected
                        ? "border-[var(--accent-primary)] bg-[var(--accent-primary)]/10 shadow-sm font-bold"
                        : "border-themed-border bg-surface-muted/40 hover:bg-surface-cardHover text-themed-textSecondary"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{lang.flag}</span>
                      <div>
                        <p className="text-xs text-themed-textPrimary font-semibold">{lang.nativeName}</p>
                        <p className="text-[10px] text-themed-textMuted font-mono">{lang.name}</p>
                      </div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-[var(--accent-primary)]" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Theme Palette Selection Grid */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-themed-textPrimary flex items-center gap-1.5">
                <Palette className="w-4 h-4 text-[var(--accent-primary)]" />
                <span>{t("layout.theme.title")} ({themes.length} {t("layout.theme.palettes")})</span>
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              {themes.map((tItem) => {
                const isSelected = tItem.id === selectedTheme;
                return (
                  <button
                    key={tItem.id}
                    type="button"
                    onClick={() => {
                      setSelectedTheme(tItem.id);
                      setTheme(tItem.id); // Live preview!
                    }}
                    className={`p-2.5 rounded-xl border text-left flex items-center justify-between gap-2 transition-all ${
                      isSelected
                        ? "border-[var(--accent-primary)] bg-[var(--accent-primary)]/10 shadow-sm"
                        : "border-themed-border bg-surface-muted/40 hover:bg-surface-cardHover hover:border-themed-borderAccent"
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base shrink-0">{tItem.icon}</span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-themed-textPrimary truncate">{tItem.name}</p>
                        <p className="text-[10px] text-themed-textMuted font-mono truncate">{tItem.category}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <div className="flex items-center -space-x-1">
                        <span
                          className="w-3 h-3 rounded-full border border-themed-borderSubtle"
                          style={{ backgroundColor: tItem.swatches.background }}
                        />
                        <span
                          className="w-3 h-3 rounded-full border border-themed-borderSubtle"
                          style={{ backgroundColor: tItem.swatches.accent }}
                        />
                      </div>
                      {isSelected && <Check className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">{t("auth.profile.displayName")}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Edward M."
              className="w-full px-3 py-2 bg-surface-input border border-themed-border rounded-xl text-sm text-themed-textPrimary placeholder:text-themed-textMuted focus:outline-none focus:border-[var(--accent-primary)] transition-colors"
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">{t("auth.profile.email")}</label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. admin@example.com"
                className="w-full pl-9 pr-3 py-2 bg-surface-input border border-themed-border rounded-xl text-sm text-themed-textPrimary placeholder:text-themed-textMuted focus:outline-none focus:border-[var(--accent-primary)] transition-colors"
              />
              <Mail className="w-4 h-4 text-themed-textMuted absolute left-3 top-2.5" />
            </div>
          </div>

          {/* Timezone Preference */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">
              {t("auth.profile.timezone")}
            </label>
            <div className="relative">
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-surface-input border border-themed-border rounded-xl text-sm text-themed-textPrimary focus:outline-none focus:border-[var(--accent-primary)] transition-colors appearance-none cursor-pointer"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value} className="bg-surface-card text-themed-textPrimary">
                    {tz.label}
                  </option>
                ))}
              </select>
              <Globe className="w-4 h-4 text-[var(--accent-primary)] absolute left-3 top-2.5 pointer-events-none" />
            </div>
          </div>

          {/* Live Timezone Preview Box */}
          <div className="p-3 rounded-xl bg-surface-muted/60 border border-themed-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--accent-primary)] animate-pulse" />
              <span className="text-xs text-themed-textSecondary">{t("auth.profile.timezone")} ({timezone}):</span>
            </div>
            <span className="font-mono text-xs font-bold text-[var(--accent-primary)]">
              {currentTimePreview}
            </span>
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-themed-borderSubtle">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-themed-textMuted hover:text-themed-textPrimary transition-colors"
            >
              {t("common.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] hover:opacity-95 text-[var(--accent-text)] font-semibold text-xs shadow-md shadow-[var(--accent-glow)] transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? t("auth.profile.saving") : t("auth.profile.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
